import React from "react";
import Modal, { ModalProps } from "components/Modal";
import {
  FetchReportTileReportKeys,
  InventoryPhysicalObjectDetailOut,
  LicenseAppType,
  SingleItem,
} from "api-client";
import Filters from "./Filters/index.tsx";
import LinearLoading from "components/LinearLoading";
import { observer } from "mobx-react-lite";
import CustomProducts from "./tabs/CustomProducts.tsx";
import SearchBar from "./SearchBar.tsx";
import DefaultProducts from "./tabs/DefaultProducts.tsx";
import {
  FiltersOptions,
  searchStore,
  SearchTabsProps,
} from "features/ProductSearch/SearchStore.ts";
import DefaultComponents from "./tabs/DefaultComponents.tsx";
import CustomComponents from "./tabs/CustomComponents.tsx";
import {
  ORGANIZATIONAL_SEARCH_TABS_LIST,
  SEARCH_TABS_LIST,
} from "../MappingEditor/consts.ts";
import { MEStore } from "features/MappingEditor/MappingEditorStore.ts";
import SearchTabList from "./SearchTabList.tsx";
import { sidebarMenuStore } from "../SidebarMenu/SidebarMenuStore.ts";
import UsedProducts from "./tabs/UsedProducts.tsx";
import clsx from "clsx";
import useFeatureFlag from "hooks/useFeatureFlag.ts";
import LoadingIcon from "components/LoadingIcon/index.tsx";
import useProductSearch from "./useProductSearch.ts";

interface ProductSearchProps extends ModalProps {
  currentRow?: InventoryPhysicalObjectDetailOut | SingleItem;
  matchProductAndUpdate?: (
    product_id: string,
    current_row: SingleItem,
    detail_view_report_keys?: FetchReportTileReportKeys
  ) => Promise<void>;
  isDetailVeiw?: boolean;
  refreshAfterMatching?: () => Promise<void>;
}

export default observer(function ProductSearch({
  open,
  close,
  currentRow,
  matchProductAndUpdate,
  isDetailVeiw,
  ...props
}: ProductSearchProps) {
  const [query, setQuery] = React.useState<string>("");
  const [filters, setFilters] = React.useState<FiltersOptions>({
    DIN_categories: [],
    materials: [],
    conformity: [],
    certifications: [],
    other_properties: [],
  });
  const { isFeatureVisible, isFeatureFlagLoading } = useFeatureFlag();

  React.useEffect(() => {
    if (open) {
      setQuery(searchStore.openProductSearch.term || "");
    }
  }, []);

  const tabList = searchStore.usingOrganisationSearch
    ? ORGANIZATIONAL_SEARCH_TABS_LIST
    : SEARCH_TABS_LIST;

  const filteredTabList = isFeatureVisible("used_products")
    ? tabList
    : tabList.filter((tab) => tab.id !== "used_products");

  const { initialCheck, loading, setLoading } = useProductSearch(tabList);

  const onSearch = (value: string | number) => {
    searchStore.setCurrentSearchPage(1);
    setQuery(String(value));
  };

  function showSelectedTabContent() {
    if (searchStore.usingOrganisationSearch) {
      switch (searchStore.selectedTab?.id) {
        case ORGANIZATIONAL_SEARCH_TABS_LIST[0].id:
          return <UsedProducts match={matchProduct} />;
        case ORGANIZATIONAL_SEARCH_TABS_LIST[1].id:
          return <CustomProducts match={matchProduct} />;
        case ORGANIZATIONAL_SEARCH_TABS_LIST[2].id:
          return <CustomComponents match={matchProduct} />;
        case ORGANIZATIONAL_SEARCH_TABS_LIST[3].id:
          return <DefaultProducts match={matchProduct} />;
        case ORGANIZATIONAL_SEARCH_TABS_LIST[4].id:
          return <DefaultComponents match={matchProduct} />;
      }
    } else {
      switch (searchStore.selectedTab?.id) {
        case SEARCH_TABS_LIST[0].id:
          return <UsedProducts match={matchProduct} />;
        case SEARCH_TABS_LIST[1].id:
          return <DefaultProducts match={matchProduct} />;
        case SEARCH_TABS_LIST[2].id:
          return <CustomProducts match={matchProduct} />;
        case SEARCH_TABS_LIST[3].id:
          return <DefaultComponents match={matchProduct} />;
        case SEARCH_TABS_LIST[4].id:
          return <CustomComponents match={matchProduct} />;
      }
    }
  }

  const matchProduct = async (product_id: string) => {
    isDetailVeiw
      ? await MEStore.detailViewMapping(product_id)
      : await matchProductAndUpdate?.(product_id, currentRow as SingleItem);
    await props.refreshAfterMatching?.();
    close();
  };

  const onClickTab = (tab: SearchTabsProps) => {
    searchStore.setIsUsedSearch(tab.id === "used_products");
    searchStore.setSelectedTab(tab);
    searchStore.setCurrentSearchPage(1);
  };

  function updateFilters(name: string, values: unknown[]) {
    setFilters((state) => ({ ...state, [name]: values }));
  }

  const isManufacturerLicence =
    sidebarMenuStore.getCurrentLicense() === LicenseAppType.Manufacturer;

  const onLoading = async () => {
    setLoading(true);
    searchStore.resetSearchResults();
    searchStore.resetCompliantItemsInfo();
    if (isFeatureFlagLoading) return;
    await initialCheck(isFeatureVisible("used_products"));
    setLoading(false);
  };

  React.useEffect(() => {
    (async () => await onLoading())();
  }, [isFeatureFlagLoading]);

  function tabContent() {
    const showTabContent =
      searchStore.isUsedSearch !== undefined &&
      !!searchStore.selectedTab?.id &&
      !loading;

    if (!showTabContent) return null;

    return (
      <>
        <Filters
          query={query}
          filters={filters}
          setFilters={updateFilters}
          containerClassName={searchStore.isUsedSearch ? "hidden" : ""}
        />
        <div
          className={clsx("flex-grow", { "pt-5": searchStore.isUsedSearch })}
        >
          {searchStore.productSearchLoading ? <LinearLoading /> : null}
          <div className="flex flex-col space-y-3">
            {showSelectedTabContent()}
          </div>
        </div>
      </>
    );
  }

  return (
    <Modal
      isOpen={open}
      setIsOpen={close}
      className="p-6"
      containerClassName="md:min-w-[1250px] min-h-[500px] overflow-visible overflow-x-hidden relative"
      position="top"
    >
      {loading && (
        <div className="absolute top-0 h-full left-0 flex w-full items-center justify-center bg-white z-3">
          <LoadingIcon type="secondary" className="!w-20 !h-20" />
        </div>
      )}

      <SearchBar value={query} onChange={onSearch} />
      <SearchTabList
        tabs={filteredTabList}
        onClickTab={onClickTab}
        hideConcularProducts={isManufacturerLicence}
      />
      {tabContent()}
    </Modal>
  );
});
